import { useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import api from '../../services/api';
import mailIcon from '../../assets/icons/correo-contacto.png';
import HeroPanelFrame from './HeroPanelFrame';

export default function HeroContactPanel({ config = {}, onClose }) {
  const [values, setValues] = useState({ nombre: '', correo: '', empresa: '', mensaje: '', website: '' });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const phone = config.telefono_publico || '';
  const whatsapp = config.whatsapp_publico || phone;
  const email = config.correo_publico || '';
  const address = config.direccion_publica || '';
  const contactDetails = [
    { label: 'WhatsApp', value: whatsapp, href: `https://wa.me/${whatsapp.replace(/\D/g, '')}` },
    { label: 'Phone', value: phone, href: `tel:${phone.replace(/[^+\d]/g, '')}` },
    { label: 'E-mail', value: email, href: `mailto:${email}` },
    { label: 'Location', value: address }
  ].filter((item) => item.value);
  const update = (event) => {
    setValues((prev) => ({ ...prev, [event.target.name]: event.target.value }));
    setResult(null);
  };
  const submit = async (event) => {
    event.preventDefault();
    if (sending) return;
    setSending(true);
    setResult(null);
    try {
      await api.post('/api/web/contacto', values, { timeout: 45000 });
      setResult({ severity: 'success', text: 'Message sent. We will reply to your e-mail.' });
      setValues({ nombre: '', correo: '', empresa: '', mensaje: '', website: '' });
    } catch (error) {
      setResult({ severity: 'error', text: error.response?.data?.error || 'We could not confirm delivery. Please try again in a few minutes.' });
    } finally {
      setSending(false);
    }
  };
  const field = (name, label, limit, extra = {}) => (
    <TextField name={name} label={label} value={values[name]} onChange={update} fullWidth size="small"
      disabled={sending} required={name !== 'empresa'} inputProps={{ maxLength: limit }} {...extra} />
  );

  return (
    <HeroPanelFrame titleId="hero-contact-title" closeLabel="Close Contact us" onClose={onClose}
      scrollable={false} sx={{ pb: 3 }}>
      <Stack component="form" onSubmit={submit} spacing={1.5} aria-busy={sending} sx={{
        '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'rgba(255,255,255,.28)' },
        '& input.MuiInputBase-input': { height: 'auto', maxWidth: 'none' },
        '& textarea.MuiInputBase-input': { minHeight: 0, maxWidth: 'none', resize: 'none' },
        '& .MuiInputLabel-root': { color: '#30443b' },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#795b29' }
      }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: 'rgba(227,208,169,.55)',
            flexShrink: 0, display: 'grid', placeItems: 'center' }}>
            <Box component="img" src={mailIcon} alt="" sx={{ width: 40, height: 40, objectFit: 'contain' }} />
          </Box>
          <Box>
            <Typography component="h2" id="hero-contact-title" sx={{ fontFamily: 'Georgia, serif', fontSize: { xs: 34, lg: 52 }, lineHeight: 1.12 }}>Contact us</Typography>
            <Typography sx={{ mt: 0.5, fontSize: 14, lineHeight: 1.4 }}>{config.contact_titulo || 'Let’s talk about your flowers.'}</Typography>
          </Box>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          {field('nombre', 'Name', 120, { autoComplete: 'name' })}
          {field('empresa', 'Company (optional)', 160, { autoComplete: 'organization' })}
        </Stack>
        {field('correo', 'E-mail', 254, { type: 'email', autoComplete: 'email' })}
        {field('mensaje', 'Message', 5000, { multiline: true, rows: 3 })}
        <Box aria-hidden="true" sx={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}>
          <label>Website<input name="website" value={values.website} onChange={update} tabIndex={-1} autoComplete="off" /></label>
        </Box>
        {result && <Alert severity={result.severity} role="status">{result.text}</Alert>}
        <Button type="submit" disabled={sending} variant="contained" sx={{ alignSelf: 'flex-start',
          bgcolor: '#183f2a', borderRadius: 999, px: 3, py: 1, textTransform: 'none', fontWeight: 800,
          '&:hover': { bgcolor: '#102d1d' } }}>{sending ? 'Sending…' : 'Send message ↗'}</Button>
        <Typography sx={{ fontSize: 12, color: '#40554b' }}>We will reply to your e-mail.</Typography>
      </Stack>
      {!!contactDetails.length && (
        <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid rgba(121,91,41,.3)' }}>
          <Typography component="h3" sx={{ mb: 1, fontSize: 12, fontWeight: 800,
            letterSpacing: 2, color: '#795b29' }}>CONTACT</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1 }}>
            {contactDetails.map((item) => (
              <Box key={item.label}>
                <Typography sx={{ fontSize: 12, color: '#40554b', mb: 0.3 }}>{item.label}</Typography>
                <Typography component={item.href ? 'a' : 'p'} href={item.href}
                  target={item.label === 'WhatsApp' ? '_blank' : undefined}
                  rel={item.label === 'WhatsApp' ? 'noopener noreferrer' : undefined}
                  sx={{ color: '#142b27', fontSize: 14, fontWeight: 600, overflowWrap: 'anywhere',
                    textDecoration: 'none', '&:hover': item.href ? { textDecoration: 'underline' } : {} }}>
                  {item.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </HeroPanelFrame>
  );
}
